/**
 * Provides an administrative interface for managing role-based Quick Links
 * configuration across the application. Allows administrators to control
 * the availability, visibility, and destination URLs of quick access links
 * for specific user roles, ensuring users have access to the appropriate
 * resources and applications based on their responsibilities.
 *
 * The component supports role and application selection, configuration of
 * link visibility settings, maintenance of application URLs, change tracking,
 * and persistence of updates to role-specific Quick Link configurations.
 */
import React, { useEffect, useState } from "react";

import { getQuickLinksConfig, updateQuickLinksConfig } from "@/services/widgets";
import { ROLE_NAME_MAP } from "@/shared/utils/maps";

type Props = {
  widget: string;
  onBack: () => void;
};

const QuickLinksConfig: React.FC<Props> = () => {
  const [data, setData] = useState<Record<string, any[]>>({});
  const [roles, setRoles] = useState<string[]>([]);

  const [role, setRole] = useState("");
  const [app, setApp] = useState("");
  const [url, setUrl] = useState("");
  const [isActive, setIsActive] = useState(true);

  const [initial, setInitial] = useState({ url: "", isActive: true });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    const res = await getQuickLinksConfig();
    setRoles(res.roles || []);
    setData(res.data || {});
  };

  useEffect(() => {
    if (role && app && data[role]) {
      const selected = data[role].find((a) => a.name === app);
      const val = selected?.url || "";
      const active = selected?.is_active ?? false;

      setUrl(val);
      setIsActive(active);
      setInitial({ url: val, isActive: active });
    } else {
      setUrl("");
      setIsActive(true);
      setInitial({ url: "", isActive: true });
    }
  }, [role, app, data]);

  const hasChanges = url !== initial.url || isActive !== initial.isActive;

  const handleSave = async () => {
    setSaving(true);

    await updateQuickLinksConfig({
      roleCode: role,
      appName: app,
      appUrl: url,
      isActive,
    });

    setInitial({ url, isActive });
    setSaving(false);
  };

  const handleCancel = () => {
    setUrl(initial.url);
    setIsActive(initial.isActive);
  };

  const apps = role ? data[role] || [] : [];

  return (
    <div className="border border-gray-200 rounded-md bg-[#fbfaf7] p-4 space-y-4">
      <div className="grid grid-cols-3 gap-4 items-center">
        <select
          className="border border-gray-200 p-2 rounded focus:outline-none focus:ring-2 focus:ring-orange-400"
          value={role}
          onChange={(e) => {
            setRole(e.target.value);
            setApp("");
          }}
        >
          <option value="">Select Role</option>
          {roles.map((r) => (
            <option key={r} value={r}>
              {ROLE_NAME_MAP[r] || r}
            </option>
          ))}
        </select>

        <select
          className="border border-gray-200 p-2 rounded focus:outline-none focus:ring-2 focus:ring-orange-400"
          value={app}
          onChange={(e) => setApp(e.target.value)}
          disabled={!role}
        >
          <option value="">Select App</option>
          {apps.map((a) => (
            <option key={a.name} value={a.name}>
              {a.name}
            </option>
          ))}
        </select>

        {app && (
          <div className="flex items-center justify-end gap-3">
            <span className="text-xs text-gray-600 font-medium">App Visibility</span>

            <div className="flex border border-gray-300 rounded overflow-hidden">
              <button
                className={`px-3 py-1 text-sm ${
                  isActive ? "bg-[var(--color-brand)]  text-white" : "bg-gray-100 text-gray-600"
                }`}
                onClick={() => setIsActive(true)}
              >
                Show
              </button>
              <button
                className={`px-3 py-1 text-sm ${
                  !isActive ? "bg-[var(--color-brand)]  text-white" : "bg-gray-100 text-gray-600"
                }`}
                onClick={() => setIsActive(false)}
              >
                Hide
              </button>
            </div>
          </div>
        )}
      </div>

      {app && isActive && (
        <div className="flex flex-col gap-1">
          <label className="text-xs text-gray-600">Configure URL</label>
          <input
            type="text"
            placeholder="Enter URL"
            className="border border-gray-200 p-2 rounded focus:outline-none focus:ring-2 focus:ring-orange-400"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
          />
        </div>
      )}

      <div className="flex justify-end items-center gap-3 pt-2">
        {hasChanges && (
          <div className="text-orange-600 border border-orange-300 bg-orange-50 px-3 py-1 rounded-md text-sm">
            Unsaved
          </div>
        )}

        <button
          className="bg-[#e7e5e0] px-4 py-1.5 rounded-md text-sm"
          onClick={handleCancel}
          disabled={!hasChanges}
        >
          Cancel
        </button>

        <button
          className="bg-[var(--color-brand)]  text-white px-4 py-1.5 rounded-md text-sm"
          onClick={handleSave}
          disabled={!role || !app || !hasChanges || saving}
        >
          {saving ? "Saving..." : "Save"}
        </button>
      </div>
    </div>
  );
};

export default QuickLinksConfig;
